import { instance as boompayClient } from "api/apiBase";
import { inboxClient } from "../../inbox/api/client";
import type {
  Mailbox,
  MailboxPropertyDefaults,
  PropertyEmailDefault,
  AssignPropertyDefaultsResponse,
  UpdateMailboxRequest,
  SmsTemplate,
  SmsVariable,
  SmsTemplateListResponse,
  SmsTemplatePayload,
  EmailSignatureDetail,
  EmailTemplate,
  EmailVariable,
  PaginatedEmailSignatures,
  PaginatedEmailTemplates,
  SaveEmailSignaturePayload,
  SignatureAssignableTemplates,
  CreateEmailTemplatePayload,
  SendTestEmailPayload,
  SendTestEmailResult,
  TemplateUsage,
  UpdateEmailTemplatePayload
} from "./types";

export const mailboxesApi = {
  list: async () => {
    const { data } = await inboxClient.get<Mailbox[]>("/api/portal/mailboxes");
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<Mailbox>(
      `/api/portal/mailboxes/${id}`
    );
    return data;
  },

  listPropertyDefaults: async (propertyIds: number[]) => {
    const { data } = await inboxClient.get<{
      property_defaults: PropertyEmailDefault[];
    }>("/api/portal/mailboxes/properties/defaults", {
      params: { property_ids: propertyIds.join(",") }
    });
    return data.property_defaults;
  },

  getSystemDefaultEmail: async () => {
    const { data } = await inboxClient.get<{
      system_default: { email: string | null } | null;
    }>("/api/portal/mailboxes/pool");
    return data.system_default?.email ?? null;
  },

  update: async (id: string, payload: UpdateMailboxRequest) => {
    const { data } = await boompayClient.patch<Mailbox>(
      `/portal/v1/communication_mailboxes/${id}`,
      payload
    );
    return data;
  },

  // CRM-1165: "default" is a per-property assignment. The PUT has REPLACE
  // semantics — property_ids is the mailbox's whole assignment, [] clears it.
  getPropertyDefaults: async (mailboxId: string) => {
    const { data } = await boompayClient.get<MailboxPropertyDefaults>(
      `/portal/v1/communication_mailboxes/${mailboxId}/property_defaults`
    );
    return data;
  },

  setPropertyDefaults: async (mailboxId: string, propertyIds: number[]) => {
    const { data } = await boompayClient.put<AssignPropertyDefaultsResponse>(
      `/portal/v1/communication_mailboxes/${mailboxId}/property_defaults`,
      { property_ids: propertyIds }
    );
    return data;
  },

  setPropertyDefault: async (propertyId: string, mailboxId: string) => {
    await boompayClient.put(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`,
      { mailbox_id: mailboxId }
    );
  },

  removePropertyDefault: async (propertyId: string) => {
    await boompayClient.delete(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`
    );
  },

  disconnect: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}`);
  },

  subscribe: async (id: string) => {
    await boompayClient.post(`/portal/v1/communication_mailboxes/${id}/sync`);
  },

  unsubscribe: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}/sync`);
  }
};

const SMS_TEMPLATES_BASE = "/portal/v1/boom_crm/sms_templates";

export const smsTemplatesApi = {
  list: async (
    page = 1,
    perPage = 100,
    search?: string,
    orderDirection?: "asc" | "desc",
    orderBy?: "updated_at" | "name",
    favoritesFirst?: boolean
  ) => {
    const { data } = await boompayClient.get<SmsTemplateListResponse>(
      SMS_TEMPLATES_BASE,
      {
        params: {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
          ...(orderDirection ? { order_direction: orderDirection } : {}),
          ...(orderBy ? { order_by: orderBy } : {}),
          ...(favoritesFirst ? { favorites_first: true } : {})
        }
      }
    );
    return data;
  },

  get: async (id: number) => {
    const { data } = await boompayClient.get<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`
    );
    return data;
  },

  resetToStandard: async (id: number) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}/reset_to_standard`
    );
    return data;
  },

  create: async (payload: SmsTemplatePayload) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      SMS_TEMPLATES_BASE,
      payload
    );
    return data;
  },

  update: async (id: number, payload: Partial<SmsTemplatePayload>) => {
    const { data } = await boompayClient.patch<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`,
      payload
    );
    return data;
  },

  duplicate: async (id: number) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}/duplicate`
    );
    return data;
  },

  delete: async (id: number) => {
    await boompayClient.delete(`${SMS_TEMPLATES_BASE}/${id}`);
  },

  setFavorite: async (id: number, favorite: boolean) => {
    const { data } = await boompayClient.put<{ favorite: boolean }>(
      `${SMS_TEMPLATES_BASE}/${id}/favorite`,
      { favorite }
    );
    return data;
  },

  usages: async (id: number) => {
    const { data } = await boompayClient.get<{ items: TemplateUsage[] }>(
      `${SMS_TEMPLATES_BASE}/${id}/usages`
    );
    return data.items;
  },

  variables: async (subjectType = "lead") => {
    const { data } = await boompayClient.get<{ variables: SmsVariable[] }>(
      `${SMS_TEMPLATES_BASE}/variables`,
      { params: { subject_type: subjectType } }
    );
    return data.variables;
  },

  render: async (
    id: number,
    contactId: number,
    options?: { leadId?: number; toNumber?: string }
  ) => {
    const { data } = await boompayClient.post<{
      rendered_body: string;
      variable_values: Record<string, string>;
    }>(`${SMS_TEMPLATES_BASE}/${id}/render`, {
      contact_id: contactId,
      lead_id: options?.leadId,
      to_number: options?.toNumber
    });
    return data;
  }
};

const EMAIL_TEMPLATES_PATH = "/portal/v1/boom_crm/email_templates";

export const emailTemplatesApi = {
  list: async (
    params?: Record<string, unknown>
  ): Promise<PaginatedEmailTemplates> => {
    const { data } = await boompayClient.get(EMAIL_TEMPLATES_PATH, { params });
    return data;
  },

  get: async (id: number): Promise<EmailTemplate> => {
    const { data } = await boompayClient.get(`${EMAIL_TEMPLATES_PATH}/${id}`);
    return data;
  },

  usages: async (id: number) => {
    const { data } = await boompayClient.get<{ items: TemplateUsage[] }>(
      `${EMAIL_TEMPLATES_PATH}/${id}/usages`
    );
    return data.items;
  },

  create: async (
    payload: CreateEmailTemplatePayload
  ): Promise<EmailTemplate> => {
    const { data } = await boompayClient.post(EMAIL_TEMPLATES_PATH, payload);
    return data;
  },

  update: async (
    id: number,
    payload: UpdateEmailTemplatePayload
  ): Promise<EmailTemplate> => {
    const { data } = await boompayClient.patch(
      `${EMAIL_TEMPLATES_PATH}/${id}`,
      payload
    );
    return data;
  },
  // Puts a Boom default email back to the subject and wording Boom seeded it with (CRM-1644).
  resetToStandard: async (id: number): Promise<EmailTemplate> => {
    const { data } = await boompayClient.post(
      `${EMAIL_TEMPLATES_PATH}/${id}/reset_to_standard`
    );
    return data;
  },

  duplicate: async (id: number): Promise<EmailTemplate> => {
    const { data } = await boompayClient.post(
      `${EMAIL_TEMPLATES_PATH}/${id}/duplicate`
    );
    return data;
  },

  delete: async (id: number) =>
    await boompayClient.delete(`${EMAIL_TEMPLATES_PATH}/${id}`),

  setFavorite: async (id: number, favorite: boolean) => {
    const { data } = await boompayClient.put<{ favorite: boolean }>(
      `${EMAIL_TEMPLATES_PATH}/${id}/favorite`,
      { favorite }
    );
    return data;
  },

  variables: async (subjectType = "lead") => {
    const { data } = await boompayClient.get<{ variables: EmailVariable[] }>(
      `${EMAIL_TEMPLATES_PATH}/variables`,
      { params: { subject_type: subjectType } }
    );
    return data.variables;
  },

  sendTest: async (
    id: number,
    payload: SendTestEmailPayload
  ): Promise<SendTestEmailResult> => {
    const { data } = await boompayClient.post<SendTestEmailResult>(
      `${EMAIL_TEMPLATES_PATH}/${id}/send_test`,
      payload
    );
    return data;
  }
};

const EMAIL_SIGNATURES_PATH = "/portal/v1/boom_crm/email_signatures";

export const emailSignaturesApi = {
  list: async (
    params?: Record<string, unknown>
  ): Promise<PaginatedEmailSignatures> => {
    const { data } = await boompayClient.get(EMAIL_SIGNATURES_PATH, {
      params
    });
    return data;
  },

  get: async (id: number): Promise<EmailSignatureDetail> => {
    const { data } = await boompayClient.get(`${EMAIL_SIGNATURES_PATH}/${id}`);
    return data;
  },

  templates: async (): Promise<SignatureAssignableTemplates> => {
    const { data } = await boompayClient.get(
      `${EMAIL_SIGNATURES_PATH}/templates`
    );
    return data;
  },

  create: async (
    payload: SaveEmailSignaturePayload
  ): Promise<EmailSignatureDetail> => {
    const { data } = await boompayClient.post(EMAIL_SIGNATURES_PATH, payload);
    return data;
  },

  update: async (
    id: number,
    payload: SaveEmailSignaturePayload
  ): Promise<EmailSignatureDetail> => {
    const { data } = await boompayClient.patch(
      `${EMAIL_SIGNATURES_PATH}/${id}`,
      payload
    );
    return data;
  },

  duplicate: async (id: number): Promise<EmailSignatureDetail> => {
    const { data } = await boompayClient.post(
      `${EMAIL_SIGNATURES_PATH}/${id}/duplicate`
    );
    return data;
  },

  delete: async (id: number) =>
    await boompayClient.delete(`${EMAIL_SIGNATURES_PATH}/${id}`)
};
